import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { TIDS } from "@/constants/testIds";
import { toast } from "sonner";
import {
  ArrowLeft, Heart, Sparkles as SparklesIcon, Flame, Lightbulb, Award,
  Send, CheckCircle2, XCircle, Copy, RefreshCw,
} from "lucide-react";

const REACTIONS = [
  { kind: "heart", Icon: Heart, label: "Love" },
  { kind: "clap", Icon: Award, label: "Clap" },
  { kind: "wow", Icon: SparklesIcon, label: "Wow" },
  { kind: "fire", Icon: Flame, label: "Fire" },
  { kind: "idea", Icon: Lightbulb, label: "Idea" },
];

const STATUS_STYLE = {
  pending: "bg-[#FFF3CD] text-[#8A6D3B] border-[#F1DFB5]",
  answered: "bg-[#E7F5EC] text-[#1E8E3E] border-[#B7E1C0]",
  skipped: "bg-[#F1F3F7] text-[#4A4D57] border-[#E4E6EC]",
};

export default function LivePanel() {
  const { id } = useParams();
  const [webinar, setWebinar] = useState(null);
  const [data, setData] = useState({ items: [], counts: {}, reactions: {} });
  const [busy, setBusy] = useState({});
  const pollRef = useRef(null);

  const loadWebinar = useCallback(async () => {
    try {
      const { data } = await api.get(`/webinars/${id}`);
      setWebinar(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  }, [id]);

  const loadQuestions = useCallback(async () => {
    try {
      const { data } = await api.get(`/webinars/${id}/live/questions`);
      setData(data);
    } catch {/* silent polling errors */}
  }, [id]);

  useEffect(() => {
    loadWebinar(); loadQuestions();
    pollRef.current = setInterval(loadQuestions, 5000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [loadWebinar, loadQuestions]);

  const runAi = async (qid) => {
    setBusy((b) => ({ ...b, [qid]: "ai" }));
    try {
      const { data: q } = await api.post(`/webinars/${id}/live/questions/${qid}/ai-answer`);
      setData((prev) => ({ ...prev, items: prev.items.map((x) => (x.id === qid ? q : x)) }));
      toast.success("AI co-pilot suggested an answer");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy((b) => ({ ...b, [qid]: null })); }
  };

  const markStatus = async (qid, status) => {
    setBusy((b) => ({ ...b, [qid]: status }));
    try {
      const { data: q } = await api.patch(`/webinars/${id}/live/questions/${qid}`, { status });
      setData((prev) => ({ ...prev, items: prev.items.map((x) => (x.id === qid ? q : x)) }));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy((b) => ({ ...b, [qid]: null })); }
  };

  const copyAnswer = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Answer copied");
  };

  if (!webinar) return <div className="text-[#4A4D57]">Loading…</div>;

  return (
    <div className="space-y-8 vp-fade-up">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <Link to={`/app/webinars/${id}`} className="inline-flex items-center gap-2 text-sm text-[#4A4D57] hover:text-[#0B5CFF]">
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Back to webinar
          </Link>
          <div className="vp-label mt-3">Live engagement</div>
          <h1 className="font-display text-3xl font-bold mt-1">{webinar.title}</h1>
          <p className="text-[#4A4D57] mt-1 text-sm">Real-time Q&amp;A + reaction feed · polls every 5s</p>
        </div>
        <button onClick={loadQuestions} className="vp-btn-ghost text-sm inline-flex items-center gap-2">
          <RefreshCw className="w-4 h-4" strokeWidth={1.5} /> Refresh
        </button>
      </div>

      {/* Reaction counters */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {REACTIONS.map(({ kind, Icon, label }) => (
          <div key={kind} className="vp-kpi flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-[#0B5CFF]/10 flex items-center justify-center">
              <Icon className="w-4 h-4 text-[#0B5CFF]" strokeWidth={1.75} />
            </div>
            <div>
              <div className="text-xs text-[#747680]">{label}</div>
              <div className="font-display text-2xl font-bold">{data.reactions[kind] || 0}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Q counts strip */}
      <div className="vp-glass p-4 flex items-center gap-4 flex-wrap">
        <div className="vp-label">Questions</div>
        <span className="text-sm">Pending: <b className="text-[#8A6D3B]">{data.counts.pending || 0}</b></span>
        <span className="text-sm">Answered: <b className="text-[#1E8E3E]">{data.counts.answered || 0}</b></span>
        <span className="text-sm">Skipped: <b className="text-[#4A4D57]">{data.counts.skipped || 0}</b></span>
      </div>

      {/* Questions list */}
      <div className="space-y-3">
        {data.items.length === 0 && (
          <div className="vp-card p-8 text-center text-[#4A4D57]">
            Waiting for questions… Share the attendee portal link to your audience.
          </div>
        )}
        {data.items.map((q) => (
          <div key={q.id} className="vp-card vp-card-hover p-5" data-testid={TIDS.liveQuestionCard}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={"inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border " + STATUS_STYLE[q.status]}>
                    {q.status}
                  </span>
                  <span className="text-xs text-[#747680]">
                    {q.attendee_name || "anon"} · {new Date(q.created_at).toLocaleTimeString("en-IN")}
                  </span>
                </div>
                <div className="font-display text-lg font-semibold mt-2 leading-snug">{q.question}</div>
                {q.ai_suggested_answer && (
                  <div className="mt-3 vp-glass p-3 border-[#0B5CFF]/30 bg-[#F1F5FF]">
                    <div className="flex items-center justify-between gap-2 mb-1.5">
                      <div className="vp-label" style={{ fontSize: "0.65rem" }}>AI co-pilot suggests</div>
                      <button onClick={() => copyAnswer(q.ai_suggested_answer)} className="text-[#4A4D57] hover:text-[#0B5CFF]" title="Copy">
                        <Copy className="w-3.5 h-3.5" strokeWidth={1.5} />
                      </button>
                    </div>
                    <div className="text-sm text-[#0B0D17] whitespace-pre-wrap leading-relaxed">{q.ai_suggested_answer}</div>
                  </div>
                )}
                {q.host_answer && (
                  <div className="mt-2 text-xs text-[#4A4D57]"><b>Host note:</b> {q.host_answer}</div>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {!q.ai_suggested_answer && (
                  <button
                    onClick={() => runAi(q.id)}
                    disabled={busy[q.id] === "ai"}
                    className="vp-btn-primary text-xs inline-flex items-center gap-1.5 py-1.5 px-3"
                    data-testid={TIDS.liveAiSuggestBtn}
                  >
                    <SparklesIcon className="w-3.5 h-3.5" strokeWidth={1.75} />
                    {busy[q.id] === "ai" ? "…" : "AI suggest"}
                  </button>
                )}
                {q.status !== "answered" && (
                  <button
                    onClick={() => markStatus(q.id, "answered")}
                    disabled={busy[q.id]}
                    className="vp-btn-ghost text-xs inline-flex items-center gap-1.5 py-1.5 px-3"
                    data-testid={TIDS.liveMarkAnsweredBtn}
                  >
                    <CheckCircle2 className="w-3.5 h-3.5" /> Mark answered
                  </button>
                )}
                {q.status !== "skipped" && (
                  <button
                    onClick={() => markStatus(q.id, "skipped")}
                    disabled={busy[q.id]}
                    className="p-1.5 text-[#4A4D57] hover:text-[#B00020]"
                    title="Skip"
                  >
                    <XCircle className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
